<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { debounce } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';
import { useDealsStore } from 'dashboard/stores/deals';
import { usePipelinesStore } from 'dashboard/stores/pipelines';
import { useMapGetter, useStore } from 'dashboard/composables/store';

import DealsListLayout from 'dashboard/components-next/Deals/DealsListLayout.vue';
import DealsKanbanBoard from 'dashboard/components-next/Deals/DealsKanbanBoard.vue';
import DealsFilterDialog from 'dashboard/components-next/Deals/DealsFilterDialog.vue';
import DealWonModal from 'dashboard/components-next/Deals/DealWonModal.vue';
import DealLostModal from 'dashboard/components-next/Deals/DealLostModal.vue';
import DealCard from 'dashboard/components-next/Deals/DealCard/DealCard.vue';
import DealsBulkActionBar from '../components/DealsBulkActionBar.vue';
import ChangeDealStageDialog from '../components/ChangeDealStageDialog.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import BulkActionsAPI from 'dashboard/api/bulkActions';

const DEBOUNCE_DELAY = 300;

const dealsStore = useDealsStore();
const pipelinesStore = usePipelinesStore();
const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const filterDialogRef = ref(null);
const wonModalRef = ref(null);
const lostModalRef = ref(null);
const bulkDeleteDialogRef = ref(null);
const changeStageDialogRef = ref(null);
const deleteDealDialogRef = ref(null);
const dealIdToDelete = ref(null);
const activeFilters = ref({ assignedToId: null, stageIds: [] });
// Kanban/lista fica na URL (?view=list) pra voltar da pagina da negociacao
// reabrir na mesma visualizacao.
const viewMode = computed(() =>
  route.query?.view === 'list' ? 'list' : 'kanban'
);
const toggleViewMode = () => {
  const query = { ...route.query };
  if (viewMode.value === 'kanban') query.view = 'list';
  else delete query.view;
  router.replace({ query });
};

// --- Bulk selection state (list and kanban views) ---
const selectedDealIds = ref([]);
const isSelecting = ref(false);
const isBulkActionLoading = ref(false);
const selectedCount = computed(() => selectedDealIds.value.length);
const hasSelection = computed(
  () => isSelecting.value || selectedCount.value > 0
);

const bulkDeleteDialogTitle = computed(() =>
  selectedCount.value > 1
    ? t('DEALS_BULK_ACTIONS.DELETE_DIALOG.TITLE')
    : t('DEALS_BULK_ACTIONS.DELETE_DIALOG.SINGULAR_TITLE')
);
const bulkDeleteDialogDescription = computed(() =>
  selectedCount.value > 1
    ? t('DEALS_BULK_ACTIONS.DELETE_DIALOG.DESCRIPTION', {
        count: selectedCount.value,
      })
    : t('DEALS_BULK_ACTIONS.DELETE_DIALOG.SINGULAR_DESCRIPTION')
);
const bulkDeleteDialogConfirmLabel = computed(() =>
  selectedCount.value > 1
    ? t('DEALS_BULK_ACTIONS.DELETE_DIALOG.CONFIRM_MULTIPLE')
    : t('DEALS_BULK_ACTIONS.DELETE_DIALOG.CONFIRM_SINGLE')
);

const clearSelection = () => {
  selectedDealIds.value = [];
  isSelecting.value = false;
};

const startSelection = () => {
  isSelecting.value = true;
};

const openBulkDeleteDialog = () => {
  if (!selectedDealIds.value.length || isBulkActionLoading.value) return;
  bulkDeleteDialogRef.value?.open?.();
};

const openChangeStageDialog = () => {
  if (!selectedDealIds.value.length || isBulkActionLoading.value) return;
  changeStageDialogRef.value?.open?.();
};

const toggleDealSelection = ({ id, value }) => {
  const isAlreadySelected = selectedDealIds.value.includes(id);
  const shouldSelect = value ?? !isAlreadySelected;

  if (shouldSelect && !isAlreadySelected) {
    selectedDealIds.value = [...selectedDealIds.value, id];
  } else if (!shouldSelect && isAlreadySelected) {
    selectedDealIds.value = selectedDealIds.value.filter(
      dealId => dealId !== id
    );
  }
};

watch(hasSelection, value => {
  if (!value) {
    bulkDeleteDialogRef.value?.close?.();
    changeStageDialogRef.value?.close?.();
  }
});

const accountId = useMapGetter('getCurrentAccountId');

const pipelineId = computed(() => Number(route.params.pipelineId));
const pipeline = computed(() =>
  pipelinesStore.getPipelineById(pipelineId.value)
);

const allDeals = computed(() => dealsStore.getDealsList);
const deals = computed(() => {
  let list = allDeals.value;
  if (activeFilters.value.assignedToId) {
    list = list.filter(
      d => d.assignedToId === activeFilters.value.assignedToId
    );
  }
  if (activeFilters.value.stageIds.length) {
    list = list.filter(d =>
      activeFilters.value.stageIds.includes(d.pipelineStageId)
    );
  }
  return list;
});
const filteredPipeline = computed(() => {
  if (!pipeline.value) return null;
  const { stageIds } = activeFilters.value;
  if (!stageIds.length) return pipeline.value;
  return {
    ...pipeline.value,
    stages: (pipeline.value.stages || []).filter(s => stageIds.includes(s.id)),
  };
});

const toggleSelectAll = shouldSelect => {
  const currentSelection = new Set(selectedDealIds.value);
  const visibleIds = deals.value.map(d => d.id);
  if (shouldSelect) {
    visibleIds.forEach(id => currentSelection.add(id));
  } else {
    visibleIds.forEach(id => currentSelection.delete(id));
  }
  selectedDealIds.value = Array.from(currentSelection);
};

const toggleStageSelection = ({ stageId, value }) => {
  const currentSelection = new Set(selectedDealIds.value);
  const stageDealIds = deals.value
    .filter(d => d.pipelineStageId === stageId)
    .map(d => d.id);
  if (value) {
    stageDealIds.forEach(id => currentSelection.add(id));
  } else {
    stageDealIds.forEach(id => currentSelection.delete(id));
  }
  selectedDealIds.value = Array.from(currentSelection);
};

const meta = computed(() => dealsStore.meta);
const isFetchingList = computed(
  () => dealsStore.uiFlags.fetchingList || pipelinesStore.uiFlags.fetchingList
);

const searchQuery = computed(() => route.query?.search || '');
const searchValue = ref(searchQuery.value);
const pageNumber = computed(() => Number(route.query?.page) || 1);

const updateURLParams = (page, search = '') => {
  const query = { ...route.query, page: page.toString() };
  if (search) query.search = search;
  else delete query.search;
  router.replace({ query });
};

const fetchDeals = async (page, search) => {
  const currentPage = page ?? pageNumber.value;
  const currentSearch = search ?? searchQuery.value;
  if (page !== undefined || search !== undefined) {
    updateURLParams(currentPage, currentSearch);
  }
  if (viewMode.value === 'kanban') {
    await dealsStore.getAll({
      search: currentSearch || undefined,
      pipeline_id: pipelineId.value || undefined,
    });
    return;
  }
  await dealsStore.get({
    page: currentPage,
    search: currentSearch || undefined,
    pipeline_id: pipelineId.value || undefined,
  });
};

const onSearch = debounce(query => {
  searchValue.value = query;
  fetchDeals(1, query);
}, DEBOUNCE_DELAY);

const onPageChange = page => fetchDeals(page, searchValue.value);

watch(pipelineId, () => fetchDeals(1, ''));
watch(viewMode, () => fetchDeals());

// Nova negociacao (opcionalmente ja na etapa clicada): pagina de criacao
const openCreate = (stage = null) => {
  router.push({
    name: 'deals_new',
    params: { accountId: accountId.value },
    query: {
      stage_id: stage?.id || undefined,
      pipeline_id: pipelineId.value || undefined,
    },
  });
};

// As automações rodam de forma assíncrona (Sidekiq); recarrega a negociação
// para refletir alterações como a atribuição de responsável.
const AUTOMATION_REFRESH_DELAY_MS = 1500;
const refreshDealAfterAutomations = dealId => {
  setTimeout(async () => {
    try {
      dealsStore.upsertDeal(await dealsStore.show(dealId));
    } catch {
      // o próximo carregamento da lista sincroniza o card
    }
  }, AUTOMATION_REFRESH_DELAY_MS);
};

const executeMoveDeal = async ({
  dealId,
  stageId,
  value: dealValue,
  lossReasonId,
} = {}) => {
  try {
    const attrs = { id: dealId, pipeline_stage_id: stageId };
    if (dealValue !== undefined) attrs.value = dealValue;
    if (lossReasonId !== undefined) attrs.loss_reason_id = lossReasonId;
    await dealsStore.update(attrs);
    refreshDealAfterAutomations(dealId);
  } catch {
    useAlert(t('DEALS.KANBAN.MOVE_ERROR'));
  }
};

const handleMoveDeal = ({ dealId, stageId }) => {
  const stages = pipeline.value?.stages || [];
  const targetStage = stages.find(s => s.id === stageId);
  const deal = allDeals.value.find(d => d.id === dealId);

  if (targetStage?.stageType === 'won') {
    wonModalRef.value?.open({ dealId, stageId, deal });
    return;
  }
  if (targetStage?.stageType === 'lost') {
    lostModalRef.value?.open({ dealId, stageId, deal });
    return;
  }
  executeMoveDeal({ dealId, stageId });
};

// Mesma pagina dedicada de "Todas as negociacoes" (kanban e lista)
const goToDealDetail = dealId => {
  router.push({
    name: 'deals_edit',
    params: { accountId: accountId.value, dealId },
  });
};

const isUpdating = computed(() => dealsStore.uiFlags.updatingItem);
const isDeleting = computed(() => dealsStore.uiFlags.deletingItem);

const handleUpdateDeal = () => {
  useAlert(t('DEALS.DETAILS.MESSAGES.UPDATED'));
};

const handleDeleteDeal = async id => {
  try {
    await dealsStore.delete(id);
    useAlert(t('DEALS.LIST.DELETE.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error.message ?? t('DEALS.LIST.DELETE.ERROR_MESSAGE'));
  }
};

const openDeleteDealDialog = dealId => {
  dealIdToDelete.value = dealId;
  deleteDealDialogRef.value?.open?.();
};

const confirmDeleteDeal = async () => {
  await handleDeleteDeal(dealIdToDelete.value);
  deleteDealDialogRef.value?.close?.();
};

const goToPipelineSettings = () => {
  router.push({
    name: 'pipelines_settings_index',
    params: { accountId: accountId.value },
  });
};

const openFilterDialog = () => {
  filterDialogRef.value?.open();
};

const navigateToManageFields = () => {
  router.push({
    name: 'attributes_list',
    params: { accountId: accountId.value },
    query: { tab: 'deal_attribute' },
  });
};

const applyFilters = filters => {
  activeFilters.value = filters;
};

const hasActiveFilters = computed(
  () =>
    !!activeFilters.value.assignedToId ||
    activeFilters.value.stageIds.length > 0
);

// --- Bulk actions ---
const assignLabels = async labels => {
  if (!labels.length || !selectedDealIds.value.length) return;

  isBulkActionLoading.value = true;
  try {
    await BulkActionsAPI.create({
      type: 'Deal',
      ids: selectedDealIds.value,
      labels: { add: labels },
    });
    useAlert(t('DEALS_BULK_ACTIONS.ASSIGN_LABELS_SUCCESS'));
    clearSelection();
    await fetchDeals();
  } catch {
    useAlert(t('DEALS_BULK_ACTIONS.ASSIGN_LABELS_FAILED'));
  } finally {
    isBulkActionLoading.value = false;
  }
};

const removeLabels = async labels => {
  if (!labels.length || !selectedDealIds.value.length) return;

  isBulkActionLoading.value = true;
  try {
    await BulkActionsAPI.create({
      type: 'Deal',
      ids: selectedDealIds.value,
      labels: { remove: labels },
    });
    useAlert(t('DEALS_BULK_ACTIONS.REMOVE_LABELS_SUCCESS'));
    clearSelection();
    await fetchDeals();
  } catch {
    useAlert(t('DEALS_BULK_ACTIONS.REMOVE_LABELS_FAILED'));
  } finally {
    isBulkActionLoading.value = false;
  }
};

const deleteDeals = async () => {
  if (!selectedDealIds.value.length) return;

  isBulkActionLoading.value = true;
  try {
    await BulkActionsAPI.create({
      type: 'Deal',
      ids: selectedDealIds.value,
      action_name: 'delete',
    });
    useAlert(t('DEALS_BULK_ACTIONS.DELETE_SUCCESS'));
    clearSelection();
    await fetchDeals();
    bulkDeleteDialogRef.value?.close?.();
  } catch {
    useAlert(t('DEALS_BULK_ACTIONS.DELETE_FAILED'));
  } finally {
    isBulkActionLoading.value = false;
  }
};

const changeStage = async pipelineStageId => {
  if (!selectedDealIds.value.length) return;

  isBulkActionLoading.value = true;
  try {
    await BulkActionsAPI.create({
      type: 'Deal',
      ids: selectedDealIds.value,
      pipeline_stage_id: pipelineStageId,
    });
    useAlert(t('DEALS_BULK_ACTIONS.CHANGE_STAGE_SUCCESS'));
    clearSelection();
    await fetchDeals();
    changeStageDialogRef.value?.close?.();
  } catch {
    useAlert(t('DEALS_BULK_ACTIONS.CHANGE_STAGE_FAILED'));
  } finally {
    isBulkActionLoading.value = false;
  }
};

const exportSelectedDeals = async () => {
  try {
    await dealsStore.export(selectedDealIds.value);
    useAlert(t('DEALS_BULK_ACTIONS.EXPORT_SUCCESS'));
  } catch {
    useAlert(t('DEALS_BULK_ACTIONS.EXPORT_FAILED'));
  }
};

onMounted(async () => {
  searchValue.value = searchQuery.value;
  store.dispatch('agents/get');
  await pipelinesStore.get();
  fetchDeals();
});
</script>

<template>
  <DealsListLayout
    :search-value="searchValue"
    :header-title="pipeline?.name || t('DEALS.HEADER')"
    :current-page="pageNumber"
    :total-items="Number(meta?.totalCount || 0)"
    :show-pagination-footer="
      viewMode === 'list' && !!deals.length && !hasSelection
    "
    :view-mode="viewMode"
    :has-active-filters="hasActiveFilters"
    show-view-toggle
    @search="onSearch"
    @update:current-page="onPageChange"
    @create="openCreate"
    @toggle-view="toggleViewMode"
    @manage-pipeline="goToPipelineSettings"
    @filter="openFilterDialog"
    @manage-fields="navigateToManageFields"
    @select="startSelection"
    @refresh="() => fetchDeals()"
  >
    <DealsBulkActionBar
      v-if="hasSelection"
      :visible-deal-ids="deals.map(d => d.id)"
      :selected-deal-ids="selectedDealIds"
      :is-loading="isBulkActionLoading"
      @toggle-all="toggleSelectAll"
      @cancel-selection="clearSelection"
      @assign-labels="assignLabels"
      @remove-labels="removeLabels"
      @export-selected="exportSelectedDeals"
      @delete-selected="openBulkDeleteDialog"
      @change-stage="openChangeStageDialog"
    />

    <template v-if="viewMode === 'kanban'">
      <DealsKanbanBoard
        v-if="filteredPipeline"
        :pipeline="filteredPipeline"
        :deals="deals"
        :is-loading="isFetchingList"
        :selectable="hasSelection"
        :selected-deal-ids="selectedDealIds"
        deletable
        @move-deal="handleMoveDeal"
        @delete-deal="openDeleteDealDialog"
        @create-in-stage="openCreate"
        @deal-click="deal => goToDealDetail(deal.id)"
        @toggle-select="toggleDealSelection"
        @toggle-stage="toggleStageSelection"
      />
      <div
        v-else-if="!isFetchingList"
        class="flex flex-col items-center justify-center p-8 gap-3"
      >
        <span class="text-n-slate-11 text-base">{{
          t('DEALS.PIPELINE.EMPTY')
        }}</span>
      </div>
    </template>

    <template v-else>
      <div v-if="isFetchingList" class="flex items-center justify-center p-8">
        <span class="text-n-slate-11 text-base">{{ t('DEALS.LOADING') }}</span>
      </div>
      <div
        v-else-if="deals.length === 0"
        class="flex flex-col items-center justify-center p-8 gap-3"
      >
        <span class="text-n-slate-11 text-base">{{
          t('DEALS.EMPTY_STATE.TITLE')
        }}</span>
        <span class="text-n-slate-10 text-sm">{{
          t('DEALS.EMPTY_STATE.SUBTITLE')
        }}</span>
      </div>
      <div
        v-else
        class="flex flex-col gap-2"
        :class="hasSelection ? 'pt-2' : ''"
      >
        <div
          class="flex items-center gap-4 px-4 py-2 text-xs font-medium uppercase tracking-wide text-n-slate-10"
        >
          <span class="flex-1 min-w-0">{{ t('DEALS.LIST.COLUMNS.NAME') }}</span>
          <span class="flex-1 min-w-0">{{
            t('DEALS.LIST.COLUMNS.CONTACT')
          }}</span>
          <span class="flex-1 min-w-0">{{
            t('DEALS.LIST.COLUMNS.COMPANY')
          }}</span>
          <span class="flex-1 min-w-0">{{
            t('DEALS.LIST.COLUMNS.STAGE')
          }}</span>
          <span class="flex-1 min-w-0">{{
            t('DEALS.LIST.COLUMNS.RESPONSIBLE')
          }}</span>
          <span class="w-28 text-right shrink-0">{{
            t('DEALS.LIST.COLUMNS.VALUE')
          }}</span>
          <div class="size-7 shrink-0" />
        </div>
        <DealCard
          v-for="deal in deals"
          :key="deal.id"
          :deal="deal"
          :is-updating="isUpdating"
          :is-deleting="isDeleting"
          :selectable="hasSelection"
          :is-selected="selectedDealIds.includes(deal.id)"
          disable-expand
          @name-click="goToDealDetail(deal.id)"
          @update-deal="handleUpdateDeal"
          @delete-deal="handleDeleteDeal"
          @select="value => toggleDealSelection({ id: deal.id, value })"
        />
      </div>
    </template>

    <Dialog
      v-if="selectedCount"
      ref="bulkDeleteDialogRef"
      type="alert"
      :title="bulkDeleteDialogTitle"
      :description="bulkDeleteDialogDescription"
      :confirm-button-label="bulkDeleteDialogConfirmLabel"
      :is-loading="isBulkActionLoading"
      @confirm="deleteDeals"
    />
    <Dialog
      ref="deleteDealDialogRef"
      type="alert"
      :title="t('DEALS_BULK_ACTIONS.DELETE_DIALOG.SINGULAR_TITLE')"
      :description="t('DEALS_BULK_ACTIONS.DELETE_DIALOG.SINGULAR_DESCRIPTION')"
      :confirm-button-label="
        t('DEALS_BULK_ACTIONS.DELETE_DIALOG.CONFIRM_SINGLE')
      "
      :is-loading="isDeleting"
      @confirm="confirmDeleteDeal"
    />
    <ChangeDealStageDialog
      v-if="selectedCount"
      ref="changeStageDialogRef"
      :is-loading="isBulkActionLoading"
      @confirm="changeStage"
    />

    <DealsFilterDialog
      ref="filterDialogRef"
      :pipeline="pipeline"
      :active-filters="activeFilters"
      @apply="applyFilters"
    />

    <DealWonModal
      ref="wonModalRef"
      @confirm="executeMoveDeal"
      @cancel="() => {}"
    />

    <DealLostModal
      ref="lostModalRef"
      @confirm="executeMoveDeal"
      @cancel="() => {}"
    />
  </DealsListLayout>
</template>
